import { useState, useEffect, useRef } from "react";
import { testimonials } from "@/mocks/testimonials";

export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const section = sectionRef.current;
    if (section) {
      const elements = section.querySelectorAll(".reveal");
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="testimonials" ref={sectionRef} className="py-20 md:py-28 lg:py-32 bg-background-100">
      <div className="px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <span className="reveal text-accent-500 text-xs font-semibold tracking-[0.25em] uppercase mb-4 block">
              Client Feedback
            </span>
            <h2 className="reveal font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              Trusted by <span className="text-primary-600">Clients Across California</span>
            </h2>
            <p className="reveal text-foreground-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Real feedback from businesses and individuals we protect every day.
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="reveal bg-background-50 rounded-xl p-8 border border-background-200/70 flex flex-col"
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="flex items-center gap-1 mb-6">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm" />
                  ))}
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed italic flex-grow mb-6">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="font-heading text-primary-700 font-bold text-sm">
                      {testimonial.name.split(" ").map((n) => n[0]).join("")}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-foreground-950 text-sm">
                      {testimonial.name}
                    </div>
                    <div className="text-foreground-500 text-xs">
                      {testimonial.title}, {testimonial.company}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Second Row */}
          <div className="hidden md:grid md:grid-cols-2 gap-6 lg:gap-8 mt-8 max-w-4xl mx-auto">
            {testimonials.slice(3, 5).map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="reveal bg-background-50 rounded-xl p-8 border border-background-200/70 flex flex-col"
                style={{ transitionDelay: `${(index + 3) * 150}ms` }}
              >
                <div className="flex items-center gap-1 mb-6">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm" />
                  ))}
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed italic flex-grow mb-6">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="font-heading text-primary-700 font-bold text-sm">
                      {testimonial.name.split(" ").map((n) => n[0]).join("")}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-foreground-950 text-sm">
                      {testimonial.name}
                    </div>
                    <div className="text-foreground-500 text-xs">
                      {testimonial.title}, {testimonial.company}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: testimonials[activeIndex].rating }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-accent-500 text-sm" />
                ))}
              </div>
              <p className="text-foreground-700 text-sm leading-relaxed italic mb-6">
                &ldquo;{testimonials[activeIndex].quote}&rdquo;
              </p>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="font-heading text-primary-700 font-bold text-sm">
                    {testimonials[activeIndex].name.split(" ").map((n) => n[0]).join("")}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-foreground-950 text-sm">
                    {testimonials[activeIndex].name}
                  </div>
                  <div className="text-foreground-500 text-xs">
                    {testimonials[activeIndex].title}, {testimonials[activeIndex].company}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <button
                  onClick={prevTestimonial}
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200 hover:bg-primary-100 transition-colors"
                  aria-label="Previous testimonial"
                >
                  <i className="ri-arrow-left-line text-foreground-600" />
                </button>
                <div className="flex items-center gap-2">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveIndex(i)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i === activeIndex ? "bg-primary-500" : "bg-background-300"
                      }`}
                      aria-label={`Go to testimonial ${i + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={nextTestimonial}
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200 hover:bg-primary-100 transition-colors"
                  aria-label="Next testimonial"
                >
                  <i className="ri-arrow-right-line text-foreground-600" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}